import React, { useState, useMemo } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from 'src/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Label } from 'src/components/ui/label';
import { PlusIcon, PencilIcon, TrashIcon } from '@phosphor-icons/react';
import useUsersByIds from 'src/api/core/users/useUsersByIds';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import config from 'src/config';
import { CompanyDTO } from 'src/api/core/companies/useCompany';

interface Alias {
  id: string;
  alias: string;
  addedByUserId?: string | null;
  createdAt: string;
}

interface CreateAliasData {
  alias: string;
}

interface CompanyAliasesTabProps {
  company: CompanyDTO;
  onRefresh?: () => void;
}

export function CompanyAliasesTab({
  company,
  onRefresh,
}: CompanyAliasesTabProps) {
  const [aliasModalOpen, setAliasModalOpen] = useState(false);
  const [editingAlias, setEditingAlias] = useState<Alias | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [aliasToDelete, setAliasToDelete] = useState<Alias | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newAlias, setNewAlias] = useState<CreateAliasData>({
    alias: '',
  });

  // Get aliases from company data
  const aliases: Alias[] = useMemo(
    () => company.aliases || [],
    [company.aliases],
  );

  // Extract unique user IDs from aliases data
  const userIds = useMemo(() => {
    if (!aliases) return [];
    const ids = aliases
      .map((alias) => alias.addedByUserId)
      .filter((id): id is string => id !== null && id !== undefined);
    return Array.from(new Set(ids));
  }, [aliases]);

  // Bulk fetch user information
  const { data: usersData } = useUsersByIds(userIds, userIds.length > 0);

  // Create a map of user ID to user info for quick lookup
  const usersMap = useMemo(() => {
    if (!usersData) return new Map();
    return new Map(usersData.map((user) => [user.id, user]));
  }, [usersData]);

  const handleCreateOrUpdateAlias = async () => {
    try {
      setIsLoading(true);
      if (editingAlias) {
        // Update existing alias
        const response = await fetch(
          `${config.apiBaseUrl}/core/companies/${company.id}/aliases/${editingAlias.id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(newAlias),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to update alias');
        }
      } else {
        // Create new alias
        const response = await fetch(
          `${config.apiBaseUrl}/core/companies/${company.id}/aliases`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(newAlias),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to create alias');
        }
      }

      onRefresh?.();
      handleModalClose();
    } catch (error) {
      console.error('Error saving alias:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditAlias = (alias: Alias) => {
    setEditingAlias(alias);
    setNewAlias({ alias: alias.alias });
    setAliasModalOpen(true);
  };

  const handleDeleteAlias = (alias: Alias) => {
    setAliasToDelete(alias);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteAlias = async () => {
    if (!aliasToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/core/companies/${company.id}/aliases/${aliasToDelete.id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error('Failed to delete alias');
      }

      onRefresh?.();
      setDeleteConfirmOpen(false);
      setAliasToDelete(null);
    } catch (error) {
      console.error('Error deleting alias:', error);
      setDeleteConfirmOpen(false);
      setAliasToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalClose = () => {
    setAliasModalOpen(false);
    setEditingAlias(null);
    setNewAlias({ alias: '' });
  };

  return (
    <>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-medium">Aliases</h2>
          <Button
            onClick={() => setAliasModalOpen(true)}
            size="sm"
            className="flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Add Alias
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <span className="text-xs font-medium text-gray-600">Alias</span>
              </TableHead>
              <TableHead>
                <span className="text-xs font-medium text-gray-600">
                  Added By
                </span>
              </TableHead>
              <TableHead>
                <span className="text-xs font-medium text-gray-600">
                  Created At
                </span>
              </TableHead>
              <TableHead>
                <span className="text-xs font-medium text-gray-600">
                  Actions
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {aliases.map((alias) => (
              <TableRow key={alias.id}>
                <TableCell>
                  <span className="text-sm font-medium">{alias.alias}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm">
                    {alias.addedByUserId
                      ? usersMap.get(alias.addedByUserId)?.name ||
                        `User ${alias.addedByUserId.slice(0, 8)}...`
                      : 'System'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm">
                    {new Date(alias.createdAt).toLocaleDateString()}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditAlias(alias)}
                      className="h-8 w-8 p-0"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAlias(alias)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {aliases.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <div className="text-center py-4">
                    <span className="text-sm text-gray-600">
                      No aliases found for this company
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Alias Creation/Edit Modal */}
      <Dialog open={aliasModalOpen} onOpenChange={handleModalClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAlias ? 'Edit Alias' : 'Add New Alias'}
            </DialogTitle>
            <DialogDescription>
              {editingAlias
                ? 'Update the alias for this company.'
                : 'Add an alternative name that this company might be known by.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="alias">Alias *</Label>
              <input
                id="alias"
                type="text"
                value={newAlias.alias}
                onChange={(e) =>
                  setNewAlias({ ...newAlias, alias: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Enter alias name"
                required
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateOrUpdateAlias}
              disabled={!newAlias.alias.trim() || isLoading}
            >
              {isLoading
                ? 'Saving...'
                : editingAlias
                  ? 'Update Alias'
                  : 'Add Alias'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteAlias}
        title="Delete Alias"
        message={`Are you sure you want to delete "${aliasToDelete?.alias}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={isDeleting}
        dangerous={true}
      />
    </>
  );
}
